import { useMemo, useState } from 'react';
import { CheckCircle2, Plus, Shuffle, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AllocationWorkspace,
  type AllocationPlanController,
} from '@/components/allocation/allocation-workspace';
import type { AllocationPlan, BookAllResult } from '@/lib/bulk-allocation/types';
import { useCaseHotelBoard } from '@/lib/cases/use-case-hotel-board';
import { useCaseOverview } from '../use-case-overview';
import { useSubCasesForCase } from '@/lib/sub-cases/api';
import { type SubCase, needsHotelAllocation } from '@/lib/sub-cases/types';
import { useAllocation, useApproveDemand, useRequestRelaxation } from './use-allocation';
import { useAutoAllocate, useBookAll } from './use-bulk-allocation';
import { usePendingHolds } from './use-holds';
import { estimateRooms, pnrLabel } from './group-utils';
import { AssignmentBoard } from './assignment-board';
import { OverrideDialog } from './override-dialog';

// Allocation tab — a thin wrapper over the shared AllocationWorkspace (the SAME
// component the disruption "Hotels" tab uses), scoped to one case. It wires the
// CASE data: the case-scoped occupancy board (useCaseHotelBoard), the case
// auto/book-all plan, the case-only wave/demand approval bar, and the manual
// per-group assignment board. The occupancy-first layout + the plan card live in
// the shared workspace, so this surface matches the disruption Hotels tab.

interface AllocationTabProps {
  caseUrn: string;
}

export function AllocationTab({ caseUrn }: AllocationTabProps) {
  const { t } = useTranslation('caseWork');
  const overview = useCaseOverview(caseUrn);
  const allocation = useAllocation(caseUrn);
  const approve = useApproveDemand(caseUrn);
  const subCasesQ = useSubCasesForCase(caseUrn);
  const holds = usePendingHolds(caseUrn);
  const board = useCaseHotelBoard(caseUrn);
  const [relaxOpen, setRelaxOpen] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);

  // The case's route origin from the BFF (never a defaulted 'SCL'); '' keeps the
  // search dialog inert until it resolves.
  const airportCode = (overview.data?.airportCode as string | null | undefined) ?? '';
  const allSubCases = useMemo<SubCase[]>(
    () => (Array.isArray(subCasesQ.data) ? subCasesQ.data : []),
    [subCasesQ.data],
  );
  // Only the groups that still need an overnight after rebooking (rebooking-first).
  const subCases = useMemo(() => allSubCases.filter(needsHotelAllocation), [allSubCases]);

  // Override (c7) targets a group; default to the first.
  const overrideGroup = subCases[0];

  // Case plan: transient auto-allocate → book-all, kept in component state and
  // reduced to the workspace's normalized plan controller.
  const auto = useAutoAllocate(caseUrn, airportCode);
  const bookAll = useBookAll(caseUrn, airportCode);
  const [plan, setPlan] = useState<AllocationPlan | null>(null);
  const [results, setResults] = useState<BookAllResult[] | null>(null);

  if (allocation.isLoading) {
    return (
      <div className="space-y-3 p-4 sm:p-6">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  if (allocation.isError || !allocation.data) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-destructive">
          {t('allocation.loadError')}
        </CardContent>
      </Card>
    );
  }

  const a = allocation.data;
  // Room counts from the BFF's single source of truth (cases need + booking
  // reservations); local pending holds (not yet reservations) ride on top.
  const roomsRequired = a.rooms?.required ?? a.roomsRequired ?? 0;
  const roomsBooked = a.rooms?.booked ?? 0;
  const roomsInFlight = (a.rooms?.held ?? 0) + holds.holds.reduce((n, h) => n + h.roomCount, 0);
  const roomsPending = a.rooms?.outstanding ?? Math.max(0, roomsRequired - roomsBooked - roomsInFlight);
  const roomsOver = a.rooms?.overAllocated ?? 0;
  // A manual search/hold is per PNR group — never sized to the whole case.
  const searchRooms = overrideGroup ? estimateRooms(overrideGroup) : 1;

  const controller: AllocationPlanController = {
    plan,
    results,
    planning: auto.isPending,
    booking: bookAll.isPending,
    buildPlan: () => {
      void (async () => {
        const next = await auto.mutateAsync();
        setPlan(next);
        setResults(null);
      })();
    },
    bookAll: () => {
      void (async () => {
        if (!plan || plan.plan.length === 0) return;
        const res = await bookAll.mutateAsync(plan);
        setResults(res.results);
      })();
    },
    buildError: auto.isError,
    bookError: bookAll.isError,
    buildLabel: t('allocation.plan.autoAllocate'),
    rebuildLabel: t('allocation.plan.reAllocate'),
    emptyHint: (
      <p className="text-muted-foreground">
        {t('allocation.plan.emptyHint.lead')}{' '}
        <span className="font-medium text-foreground">{t('allocation.plan.autoAllocate')}</span>{' '}
        {t('allocation.plan.emptyHint.middle')}{' '}
        <span className="font-medium text-foreground">{t('allocation.plan.bookAll')}</span>.
      </p>
    ),
  };

  // Case-only wave/demand approval bar (no event analog) — passed to the workspace.
  const waveBar = (
    <Card className="shrink-0">
      <CardHeader className="flex flex-row items-center justify-between gap-4 pb-3">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <span>
              {t('allocation.wave.title', {
                wave: a.currentWave,
                status: a.demandApproved
                  ? t('allocation.wave.approved')
                  : t('allocation.wave.pendingApproval'),
              })}
            </span>
            <Badge variant="neutral" size="sm" className="font-normal text-xs">
              {a.currentWave === 1
                ? t('allocation.wave.initialScope')
                : t('allocation.wave.extended', { wave: a.currentWave })}
            </Badge>
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('allocation.wave.hint')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={a.demandApproved || approve.isPending}
            onClick={() => void approve.mutateAsync(a.currentWave)}
          >
            <CheckCircle2 className="mr-1 h-3 w-3" aria-hidden />
            {a.demandApproved
              ? t('allocation.wave.approved')
              : t('allocation.actions.approveInitialDemand')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!a.demandApproved || approve.isPending}
            onClick={() => void approve.mutateAsync(a.currentWave + 1)}
          >
            <Plus className="mr-1 h-3 w-3" aria-hidden />
            {t('allocation.actions.addWave')}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setOverrideOpen(true)}>
            <Shuffle className="mr-1 h-3 w-3" aria-hidden />
            {t('allocation.override.title')}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setRelaxOpen(true)}>
            <TrendingUp className="mr-1 h-3 w-3" aria-hidden />
            {t('allocation.actions.requestRelaxation')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-4 gap-x-6 gap-y-2 text-sm">
          <div>
            <dt className="text-xs uppercase text-muted-foreground">{t('allocation.rooms.required')}</dt>
            <dd className="text-lg font-semibold">{roomsRequired}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-muted-foreground">{t('allocation.rooms.booked')}</dt>
            <dd className="text-lg font-semibold text-emerald-600">{roomsBooked}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-muted-foreground">{t('allocation.rooms.inFlight')}</dt>
            <dd className="text-lg font-semibold text-amber-600">{roomsInFlight}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-muted-foreground">{t('allocation.rooms.pending')}</dt>
            <dd className="text-lg font-semibold" data-testid="allocation-rooms-pending">{roomsPending}</dd>
          </div>
          {roomsOver > 0 && (
            <div>
              <dt className="text-xs uppercase text-destructive">{t('allocation.rooms.overAllocated')}</dt>
              <dd className="text-lg font-semibold text-destructive" data-testid="allocation-rooms-over">
                {roomsOver}
              </dd>
            </div>
          )}
        </dl>
      </CardContent>
    </Card>
  );

  return (
    <>
      <AllocationWorkspace
        board={board}
        controller={controller}
        waveBar={waveBar}
        manualBoard={
          <AssignmentBoard
            caseUrn={caseUrn}
            subCases={subCases}
            airportCode={airportCode}
            holds={holds}
          />
        }
      />

      <OverrideDialog
        open={overrideOpen}
        onClose={() => setOverrideOpen(false)}
        caseUrn={caseUrn}
        subCaseUrn={overrideGroup?.urn}
        subCaseLabel={overrideGroup ? pnrLabel(overrideGroup.pnrUrn) : undefined}
        airportCode={airportCode}
        rooms={searchRooms}
      />
      <RelaxationDialog open={relaxOpen} onClose={() => setRelaxOpen(false)} caseUrn={caseUrn} />
    </>
  );
}

interface RelaxationDialogProps {
  caseUrn: string;
  open: boolean;
  onClose: () => void;
}

function RelaxationDialog({ caseUrn, open, onClose }: RelaxationDialogProps) {
  const { t } = useTranslation('caseWork');
  const relax = useRequestRelaxation();
  const [axis, setAxis] = useState<'cost' | 'distance'>('cost');
  const [amount, setAmount] = useState(10);
  const [reason, setReason] = useState('');

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('allocation.relaxation.title')}</DialogTitle>
          <DialogDescription>{t('allocation.relaxation.description')}</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3 text-sm"
          onSubmit={async (e) => {
            e.preventDefault();
            await relax.mutateAsync({ caseUrn, axis, amount, reason });
            onClose();
          }}
        >
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="text-xs uppercase text-muted-foreground">
                {t('allocation.relaxation.axis')}
              </span>
              <select
                className="w-full rounded-md border border-input bg-background px-2 py-1.5"
                value={axis}
                onChange={(e) => setAxis(e.target.value as 'cost' | 'distance')}
              >
                <option value="cost">{t('allocation.relaxation.axisCost')}</option>
                <option value="distance">{t('allocation.relaxation.axisDistance')}</option>
              </select>
            </label>
            <label className="space-y-1">
              <span className="text-xs uppercase text-muted-foreground">
                {t('allocation.relaxation.amount')}
              </span>
              <input
                type="number"
                min={1}
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                className="w-full rounded-md border border-input bg-background px-2 py-1.5"
              />
            </label>
          </div>
          <label className="block space-y-1">
            <span className="text-xs uppercase text-muted-foreground">
              {t('allocation.relaxation.reason')}
            </span>
            <textarea
              className="w-full rounded-md border border-input bg-background px-2 py-1.5"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              placeholder={t('allocation.relaxation.reasonPlaceholder')}
            />
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common:common.cancel')}
            </Button>
            <Button type="submit" disabled={relax.isPending || !reason}>
              {relax.isPending ? t('shared.submitting') : t('allocation.relaxation.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
